import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { CrtImportHistoryButtons } from '@/components/CrtImportHistoryButtons'
import { CrtImportPeriodNotFound } from '@/components/CrtImportPeriodNotFound'
import { DetailsHeader } from '@/components/DetailsHeader'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import {
  channelMixDetailsExportQueryOptions,
  channelMixDetailsRawExportQueryOptions,
  channelMixDetailsSearchSchema,
  currentChannelMixQueryOptions,
  useChannelMixById,
  useUpdateChannelMix,
} from '@/hooks/queries/crt/useChannelMix'
import { useLastUpdatedChannelMixLog } from '@/hooks/queries/useAuditTrails'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useTablePagination } from '@/hooks/useTablePagination'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { crtPeriodFromParams } from '@/lib/utils/crtPeriodParams'
import { queryClient } from '@/main'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { UpdateChannelMix } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ChannelMixDetailsDataTable from './-components/ChannelMixDetails/ChannelMixDetailsDataTable'

export const Route = createFileRoute(
  '/_auth/_modules/crt/pricing-excise/data-sources/channel-mix/$year/$month',
)({
  component: ChannelMixPeriod,
  validateSearch: (search) => channelMixDetailsSearchSchema.parse(search),
  loader: async ({ params }) => {
    const crtPeriod = crtPeriodFromParams(params)
    const channelMix = crtPeriod
      ? await queryClient.fetchQuery(currentChannelMixQueryOptions(crtPeriod))
      : null

    return {
      crumb: channelMix?.name ?? `${params.year}/${params.month}`,
      crtPeriod,
      channelMixId: channelMix ? String(channelMix.id) : null,
    }
  },
})

function ChannelMixPeriod() {
  const { formatMonthYear } = useLocalizedDate()
  const { year, month } = Route.useParams()
  const { crtPeriod, channelMixId } = Route.useLoaderData()

  if (!channelMixId) {
    return (
      <CrtImportPeriodNotFound
        period={crtPeriod ? formatMonthYear(parseDateOnly(crtPeriod)) : `${year}/${month}`}
        otherPeriodsTo="/crt/pricing-excise/data-sources/channel-mix"
      />
    )
  }

  return <ChannelMixDetails key={channelMixId} channelMixId={channelMixId} />
}

function ChannelMixDetails({ channelMixId }: { channelMixId: string }) {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonthYear } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const {
    materialGroupCode,
    brand,
    productType,
    province,
    distributionChannel,
    marked,
    keyword,
    sort,
  } = search
  const apiQueries = {
    materialGroupCode,
    brand,
    productType,
    province,
    distributionChannel,
    marked,
    keyword,
    sort,
  }

  const { data, isPending, isError } = useChannelMixById(channelMixId, {
    ...apiQueries,
    page: search.page ?? 1,
    pageSize,
  })
  const { data: lastUpdatedChannelMixTrail } = useLastUpdatedChannelMixLog(channelMixId)

  const [formState, setFormState] = useState({
    name: '',
    description: '',
  })

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
      })
    }
  }, [data])

  const getUnsavedChanges = (): UpdateChannelMix | null => {
    if (!data) return null

    const changes: UpdateChannelMix = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = getUnsavedChanges() !== null

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedChannelMixTrail)

  const { mutate: updateChannelMix, isPending: isUpdatePending } = useUpdateChannelMix({
    channelMixId: Number(channelMixId),
  })

  const handleUpdateName = (name: string) => {
    setFormState((prev) => ({ ...prev, name }))
  }

  const handleUpdateDescription = (description: string) => {
    setFormState((prev) => ({ ...prev, description }))
  }

  const handleSave = () => {
    const unsavedChanges = getUnsavedChanges()
    if (!unsavedChanges) return

    updateChannelMix(unsavedChanges)
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <Text>{t('errors.channelMix.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={hasUnsavedChanges} />

      <HStack gap="sm" className="items-start justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="crt_channel_mix_edit"
        />

        <VStack gap="md" className="items-end">
          <Text className="text-muted-foreground text-sm">
            {t('crtPeriod', { ns: 'crt' })}: {formatMonthYear(parseDateOnly(data.crtPeriod))}
          </Text>
          <HStack gap="sm">
            <ExportCsvMenu
              items={[
                <ExportCsvMenuItem
                  label={t('export.viewData', { ns: 'common' })}
                  description={t('export.viewDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      channelMixDetailsExportQueryOptions(channelMixId, apiQueries),
                    )
                  }
                  rowCount={data.detailsRowCount}
                />,
                <ExportCsvMenuItem
                  label={t('export.rawData', { ns: 'common' })}
                  description={t('export.rawDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      channelMixDetailsRawExportQueryOptions(channelMixId),
                    )
                  }
                  rowCount={data.detailsRawRowCount}
                />,
              ]}
            />
            <CrtImportHistoryButtons
              otherPeriodsTo="/crt/pricing-excise/data-sources/channel-mix"
              uploadType="import-channel-mix-details"
              uploadAttributes={{ 'channel-mix-id': channelMixId }}
            />
          </HStack>
        </VStack>
      </HStack>

      <ChannelMixDetailsDataTable
        channelMixId={channelMixId}
        channelMix={data}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <PermissionsGuard required={['crt_channel_mix_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdatePending}
          lastUpdatedLog={lastUpdatedLog}
          onSaveChanges={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
